import React, { useState, useEffect } from 'react';

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (searchData: any) => void;
  initialData?: {
    query: string;
    filters?: Record<string, any>;
    searchMode?: string;
    searchScope?: string;
    queryType?: string;
  };
}

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [searchName, setSearchName] = useState('');
  const [description, setDescription] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);

  console.log('SaveSearchModal render - isOpen:', isOpen);

  useEffect(() => {
    console.log('SaveSearchModal useEffect - isOpen changed to:', isOpen);
    if (isOpen) {
      setSearchName('');
      setDescription('');
      setIsFavorite(false);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchName.trim()) return;

    const searchFilters = {
      ...initialData?.filters,
      searchMode: initialData?.searchMode,
      searchScope: initialData?.searchScope,
      queryType: initialData?.queryType
    };

    onSave({
      search_name: searchName,
      description: description || undefined,
      search_query: initialData?.query || '',
      search_filters: searchFilters,
      is_favorite: isFavorite,
      search_type: 'opensearch'
    });
  };

  if (!isOpen) return null;

  // Inline styles matching the project's style
  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#1B365D'
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '20px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: '80px'
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px'
  };

  const checkboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    marginRight: '8px',
    cursor: 'pointer'
  };

  const infoBoxStyle: React.CSSProperties = {
    backgroundColor: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #e5e7eb'
  };

  const infoTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#1f2937',
    marginBottom: '8px',
    fontWeight: '600'
  };

  const codeStyle: React.CSSProperties = {
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: 'white',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    marginTop: '4px',
    wordBreak: 'break-all',
    color: '#1f2937'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151'
  };

  const saveButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#059669',
    color: 'white'
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Save Search</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Search Name *
            </label>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              style={inputStyle}
              placeholder="Enter a name for this search"
              required
              onFocus={(e) => {
                e.target.style.borderColor = '#059669';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={textareaStyle}
              placeholder="Optional description for this search"
              onFocus={(e) => {
                e.target.style.borderColor = '#059669';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>

          {initialData && (
            <div style={infoBoxStyle}>
              <p style={infoTextStyle}>Query to save:</p>
              <div style={codeStyle}>
                {initialData.query || '(empty query)'}
              </div>
              
              {initialData.filters && Object.keys(initialData.filters).some(key => initialData.filters![key] !== 'All') && (
                <>
                  <p style={{...infoTextStyle, marginTop: '12px'}}>Filters:</p>
                  <div style={codeStyle}>
                    {Object.entries(initialData.filters)
                      .filter(([_, value]) => value !== 'All')
                      .map(([key, value]) => (
                        <div key={key}>
                          <span style={{fontWeight: '600', textTransform: 'capitalize'}}>{key}:</span> {value}
                        </div>
                      ))
                    }
                  </div>
                </>
              )}
              
              {(initialData.searchMode || initialData.searchScope || initialData.queryType) && (
                <>
                  <p style={{...infoTextStyle, marginTop: '12px'}}>Search Settings:</p>
                  <div style={codeStyle}>
                    {initialData.searchMode && (
                      <div><span style={{fontWeight: '600'}}>Mode:</span> {initialData.searchMode}</div>
                    )}
                    {initialData.searchScope && (
                      <div><span style={{fontWeight: '600'}}>Scope:</span> {initialData.searchScope}</div>
                    )}
                    {initialData.queryType && (
                      <div><span style={{fontWeight: '600'}}>Query Type:</span> {initialData.queryType.toUpperCase()}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div style={checkboxContainerStyle}>
            <input
              type="checkbox"
              id="isFavorite"
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
              style={checkboxStyle}
            />
            <label htmlFor="isFavorite" style={{fontSize: '14px', color: '#374151', cursor: 'pointer'}}>
              Mark as favorite
            </label>
          </div>

          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={onClose}
              style={cancelButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={saveButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#047857';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
            >
              Save Search
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveSearchModal;